import { useEffect, useMemo, useRef, useState } from 'react';

import isSet from '../../Helpers/isSet';
import { addJornayaScript, createJornayaInput, JORNAYA_INPUT_ID } from '../../Helpers/jornaya';
import { useScreenSizeCheck } from '../../Helpers/screen-size-helpers';
import styleStringToObj from '../../Helpers/styleStringToObj';
import getInsuranceTypesService from '../../Services/getInsuranceTypesService';

import InputInsurancePicker from '../../Components/InputInsurancePicker';
import InputZipcode from '../../Components/InputZipcode';

import content from '../../../../../public/js/landing_page_content.json';
import fallbackPickerOptions from '../../data/insurancePickerData';

const MobileFormView = ({ onSubmit }) => {
    const isCallCenterAvailable = window?.call_center_available ?? true;
    const phone = window?.lead_phone || '';

    return (
        <div className="mobile-form">
            {isCallCenterAvailable && (
                <button type="button">
                    <a href={`tel:${phone}`}>
                        <b className="call">CONNECT ME NOW</b>
                    </a>
                </button>
            )}
            <button type="submit" btn-type={isCallCenterAvailable ? 'secondary' : 'primary'} onClick={onSubmit}>
                GET STARTED
            </button>
        </div>
    );
};

const FormView = ({ useFormData, onSubmit }) => {
    const [formData, setFormData, showError] = useFormData();
    const { inputs } = content;

    useEffect(() => {
        sessionStorage.removeItem('formData');
        setFormData(() => {
            return {
                ...formData,
                quoteOrigin: window.location.pathname,
                origin_page_id: window?.page_id || 0,
            };
        });
    }, []);

    const inputFields = {
        zipcode: InputZipcode,
        insurancePicker: InputInsurancePicker,
    };

    const handleInputChange = ({ name, element, isValid = true }) => {
        setFormData(prevData => {
            let thisInvalidList = prevData.invalidFields || [];
            if (!isValid) {
                if (!thisInvalidList.includes(name)) {
                    thisInvalidList.push(name);
                }
            } else {
                thisInvalidList = thisInvalidList.filter(invalid => invalid !== name);
            }
            const thisData = {
                ...prevData,
                [name]: element.value,
                invalidFields: thisInvalidList,
            };
            return thisData;
        });
    };

    useEffect(() => {
        // jornaya compliance script
        addJornayaScript();

        /* this element gets populated with the leadid token value, which we send to the create lead API */
        createJornayaInput('lead-form');

        setTimeout(() => {
            const leadIdToken = document.getElementById(JORNAYA_INPUT_ID)?.value;
            setFormData(prevData => ({
                ...prevData,
                jornaya_id: leadIdToken,
            }));
        }, 1000);
        getInsuranceTypesService(window?.page_id || 0).then(insuranceTypes => {
            setFormData(prevData => {
                return {
                    ...prevData,
                    cachedInputTypes: insuranceTypes,
                    insurance_types: insuranceTypes.filter(type => type.selected),
                };
            });
        });
    }, []);

    const cachedInputTypes = useMemo(() => {
        if (!formData?.cachedInputTypes) {
            return fallbackPickerOptions;
        }
        return formData?.cachedInputTypes;
    }, [formData?.cachedInputTypes]);

    const handleSubmit = e => {
        e.preventDefault();
        onSubmit(e);
    };

    return (
        <form id="lead-form" onSubmit={handleSubmit}>
            {inputs.map((input, index) => {
                const { type, requirementText, name, label, placeholder, required } = input;
                const inlineStyle = styleStringToObj(input.inlineStyle || '');
                const Component = inputFields[type];
                const errorText =
                    required && !isSet(formData[name])
                        ? requirementText
                            ? requirementText
                            : 'This field is required'
                        : '';
                return (
                    <Component
                        key={index}
                        requirementText={requirementText}
                        label={label}
                        type={type}
                        name={name}
                        isRequired={required}
                        value={formData[name] || ''}
                        externalErrorMessage={errorText}
                        showError={showError}
                        placeholder={placeholder}
                        required={required}
                        inlineStyle={inlineStyle}
                        onChange={(e, valid) =>
                            handleInputChange({
                                type,
                                name,
                                element: e.target,
                                isValid: valid,
                            })
                        }
                        pageId={window?.page_id || 0}
                        externalInsuranceTypes={cachedInputTypes}
                    />
                );
            })}
            <button className="start-quote" type="submit">
                GET STARTED
            </button>
        </form>
    );
};

const FormBusiness = ({ useFormData }) => {
    const [formData, setFormData, showErrorDefault] = useFormData();
    const [showError, setShowError] = useState(showErrorDefault);
    const isLargeScreen = useScreenSizeCheck(481);
    const isCallCenterAvailable = window?.call_center_available ?? true;

    const requiredFields = useRef(content.inputs.filter(input => input.required).map(input => input.name) || []);

    const handleStartQuote = (e, doVerify = true) => {
        e.preventDefault();
        let ok = true;
        if (doVerify) {
            setShowError(false);
            const missingFields = requiredFields.current.filter(field => {
                const fieldValue = formData[field];
                return !isSet(fieldValue);
            });
            if (missingFields.length > 0 || formData.invalidFields?.length > 0) {
                ok = false;
                setTimeout(() => {
                    setShowError(true);
                }, 100);
                return;
            }
        }
        if (ok) {
            sessionStorage.setItem('formData', JSON.stringify(formData));
            window.location.href = '/my-quote';
        }
    };

    return (
        <>
            {isCallCenterAvailable && !isLargeScreen && <MobileFormView onSubmit={e => handleStartQuote(e, false)} />}
            {(!isCallCenterAvailable || isLargeScreen) && (
                <FormView useFormData={() => [formData, setFormData, showError]} onSubmit={handleStartQuote} />
            )}
        </>
    );
};

export default FormBusiness;
